import { useState } from 'react';
import { entero } from '../lib/formato.js';
import {
  CLS_EPP,
  CLS_IDC_ESTATUS,
  CLS_SITIO,
  txt,
  TXT_EPP,
  TXT_IDC_ESTATUS,
  TXT_IDC_MODALIDAD,
  TXT_SITIO,
} from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Formulario, type Campo } from '../piezas/Formulario.jsx';
import { Tabla, type Columna } from '../piezas/Tabla.jsx';
import { useMask, useSesion } from '../piezas/Sesion.jsx';
import { TarifasIdc } from '../piezas/TarifasIdc.jsx';

/**
 * Padron de sitios y plantilla de IDC.
 */

/* --------------------------------- Sitios --------------------------------- */

interface Sitio extends Record<string, unknown> {
  id: string;
  cliente: string;
  determinante: string;
  nombre: string;
  direccion: string | null;
  cp: string | null;
  ciudad: string | null;
  estado: string | null;
  estado_abrev: string | null;
  municipio: string | null;
  tipo_sitio: string | null;
  formato: string | null;
  estatus: string;
  duplicado_de_id: string | null;
  duplicado_de_determinante: string | null;
  estado_deducido: boolean;
  incompleto: boolean;
}

const CAMPOS_SITIO: Campo[] = [
  {
    nombre: 'cliente_id',
    etiqueta: 'Cliente',
    tipo: 'catalogo',
    catalogo: 'clientes',
    requerido: true,
    soloAlta: true,
  },
  {
    nombre: 'determinante',
    etiqueta: 'Determinante',
    tipo: 'texto',
    requerido: true,
    soloAlta: true,
    ayuda: 'Como la operacion del cliente nombra al sitio. Es la llave de deduplicacion.',
  },
  { nombre: 'nombre', etiqueta: 'Nombre del sitio', tipo: 'texto', requerido: true, ancho: true },
  { nombre: 'direccion', etiqueta: 'Direccion', tipo: 'texto-largo' },
  { nombre: 'cp', etiqueta: 'Codigo postal', tipo: 'texto', patron: '[0-9]{5}' },
  { nombre: 'municipio', etiqueta: 'Municipio', tipo: 'texto' },
  {
    nombre: 'ciudad_id',
    etiqueta: 'Ciudad',
    tipo: 'catalogo',
    catalogo: 'ciudades',
    ayuda: 'De aqui se deduce el estado si no se captura.',
  },
  { nombre: 'estado_id', etiqueta: 'Estado', tipo: 'catalogo', catalogo: 'estados' },
  { nombre: 'tipo_sitio_id', etiqueta: 'Tipo de sitio', tipo: 'catalogo', catalogo: 'tipos-sitio' },
  { nombre: 'formato_id', etiqueta: 'Formato', tipo: 'catalogo', catalogo: 'formatos-sitio' },
  {
    nombre: 'estatus',
    etiqueta: 'Estatus',
    tipo: 'lista',
    valores: Object.entries(TXT_SITIO).map(([valor, etiqueta]) => ({ valor, etiqueta })),
    ancho: true,
    ayuda: 'El duplicado lo marca el servidor al detectarlo; no se pone a mano.',
  },
];

export function Sitios(): JSX.Element {
  const { puede } = useSesion();
  const [panel, setPanel] = useState<{ fila?: Sitio } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const escribe = puede('MOD_SITIOS', 'escribir');

  const columnas: Columna<Sitio>[] = [
    {
      campo: 'determinante',
      titulo: 'Determinante',
      ordenable: true,
      celda: (s) => <span className="folio">{s.determinante}</span>,
    },
    {
      campo: 'nombre',
      titulo: 'Sitio',
      ordenable: true,
      celda: (s) => (
        <>
          {s.nombre}
          <div className="muted" style={{ fontSize: 11 }}>
            {s.cliente}
          </div>
        </>
      ),
    },
    {
      campo: 'ciudad',
      titulo: 'Ubicacion',
      celda: (s) => (
        <>
          {s.ciudad ?? <span className="muted">sin ciudad</span>}
          {(s.municipio || s.estado) && (
            <div className="muted" style={{ fontSize: 11 }}>
              {[s.municipio, s.estado_abrev ?? s.estado].filter(Boolean).join(', ')}
              {/* Un dato deducido se dice deducido. Una deduccion que se
                  presenta como captura es un dato que nadie vuelve a revisar. */}
              {s.estado_deducido && (
                <span className="tag t-cold" style={{ marginLeft: 4 }}>
                  estado deducido
                </span>
              )}
            </div>
          )}
        </>
      ),
    },
    { campo: 'tipo_sitio', titulo: 'Tipo', secundaria: true },
    { campo: 'formato', titulo: 'Formato', secundaria: true },
    {
      campo: 'estatus',
      titulo: 'Estatus',
      ordenable: true,
      celda: (s) => (
        <>
          <span className={`tag ${CLS_SITIO[s.estatus] ?? 't-mute'}`}>
            {txt(TXT_SITIO, s.estatus)}
          </span>
          {/* El duplicado se marca, no se descarta, asi que la pantalla dice
              de QUE sitio es duplicado: sin eso la marca no sirve para
              decidir cual de los dos se queda. */}
          {s.duplicado_de_determinante && (
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              de <span className="folio">{s.duplicado_de_determinante}</span>
            </div>
          )}
          {s.incompleto && !s.duplicado_de_id && (
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              falta direccion, CP, ciudad o tipo
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Pagehead />
      <Tabla<Sitio>
        ruta="sitios"
        que="los sitios"
        icono="📍"
        columnas={columnas}
        llave={(s) => s.id}
        buscable
        csv
        orden="determinante"
        recarga={recarga}
        {...(escribe ? { alElegir: (fila: Sitio) => setPanel({ fila }) } : {})}
        acciones={
          escribe ? (
            <button
              type="button"
              className="btn pri"
              onClick={() => setPanel({})}
              data-prueba="alta"
            >
              + Sitio
            </button>
          ) : undefined
        }
      />

      {panel && (
        <Formulario
          ruta="sitios"
          campos={CAMPOS_SITIO}
          titulo={panel.fila ? `Sitio ${panel.fila.determinante}` : 'Alta de sitio'}
          actual={panel.fila}
          llave={panel.fila?.id}
          alCerrar={() => setPanel(null)}
          alGuardar={() => {
            setPanel(null);
            setRecarga(recarga + 1);
          }}
        />
      )}
    </>
  );
}

/* ----------------------------------- IDC ----------------------------------- */

interface Idc extends Record<string, unknown> {
  clave: string;
  nombre: string;
  telefono: string | null;
  ciudad: string | null;
  modalidad: string;
  estatus: string;
  epp: string | null;
  horas_mes: number | null;
  costo_mensual_centavos?: number;
  moneda?: string;
  tiene_resguardo: boolean;
  piezas_resguardo: number;
  tickets_abiertos: number;
}

const CAMPOS_IDC: Campo[] = [
  {
    nombre: 'clave',
    etiqueta: 'Clave',
    tipo: 'texto',
    requerido: true,
    soloAlta: true,
    patron: 'idc_[a-z0-9_]{3,20}',
    ayuda: 'idc_ + tres a veinte letras.',
  },
  { nombre: 'nombre', etiqueta: 'Nombre completo', tipo: 'texto', requerido: true, ancho: true },
  {
    nombre: 'modalidad',
    etiqueta: 'Modalidad',
    tipo: 'lista',
    requerido: true,
    valores: Object.entries(TXT_IDC_MODALIDAD).map(([valor, etiqueta]) => ({ valor, etiqueta })),
  },
  {
    nombre: 'estatus',
    etiqueta: 'Estatus',
    tipo: 'lista',
    valores: Object.entries(TXT_IDC_ESTATUS).map(([valor, etiqueta]) => ({ valor, etiqueta })),
  },
  {
    nombre: 'telefono',
    etiqueta: 'Telefono',
    tipo: 'texto',
    patron: '\\+[1-9][0-9]{7,14}',
    ayuda: 'Con lada internacional: +521234567890.',
  },
  { nombre: 'ciudad_id', etiqueta: 'Ciudad base', tipo: 'catalogo', catalogo: 'ciudades' },
  {
    nombre: 'curp',
    etiqueta: 'CURP',
    tipo: 'texto',
    ayuda: 'Dieciocho caracteres. Se guarda cifrada.',
  },
  {
    nombre: 'tipo_identificacion_id',
    etiqueta: 'Identificacion',
    tipo: 'catalogo',
    catalogo: 'tipos-identificacion',
  },
  { nombre: 'numero_identificacion', etiqueta: 'Numero de identificacion', tipo: 'texto' },
  { nombre: 'direccion', etiqueta: 'Direccion', tipo: 'texto-largo' },
  {
    nombre: 'epp',
    etiqueta: 'Equipo de proteccion',
    tipo: 'lista',
    valores: Object.entries(TXT_EPP).map(([valor, etiqueta]) => ({ valor, etiqueta })),
  },
  {
    nombre: 'horas_mes',
    etiqueta: 'Horas al mes',
    tipo: 'numero',
    min: 1,
    max: 320,
    ayuda: 'Quien trabaja 160 no se costea como quien trabaja 176.',
  },
  {
    nombre: 'costo_mensual_centavos',
    etiqueta: 'Costo mensual',
    tipo: 'dinero',
    ayuda: 'En pesos. Obligatorio para un IDC fijo por sueldo.',
  },
  {
    nombre: 'tiene_resguardo',
    etiqueta: 'Lleva resguardo',
    tipo: 'si-no',
    ancho: true,
    ayuda: 'Si trae equipo encima y por tanto tiene almacen propio.',
  },
];

export function Idcs(): JSX.Element {
  const { puede, veMontos, veDatosPersonales } = useSesion();
  const [panel, setPanel] = useState<{ fila?: Idc } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const mask = useMask();
  const escribe = puede('MOD_IDC', 'escribir');

  /* Los campos que el perfil no recibe se quitan del FORMULARIO, no se
     muestran vacios: un campo vacio que al guardar borra el dato que si
     existia es peor que no poder editar. El servidor tampoco los manda.
  
     Aplica igual al dinero y a los datos personales. El CURP y el numero de
     identificacion van cifrados en reposo y solo los reciben los perfiles con
     `ve_datos_personales`; un despachador asigna visitas, no valida
     identidades. */
  const SOLO_MONTOS = ['costo_mensual_centavos'];
  const SOLO_PERSONALES = ['curp', 'numero_identificacion'];
  const campos = CAMPOS_IDC.filter(
    (c) =>
      (veMontos || !SOLO_MONTOS.includes(c.nombre)) &&
      (veDatosPersonales || !SOLO_PERSONALES.includes(c.nombre)),
  );

  const columnas: Columna<Idc>[] = [
    {
      campo: 'nombre',
      titulo: 'Ingeniero',
      ordenable: true,
      celda: (i) => (
        <>
          {i.nombre}
          <div className="muted folio" style={{ fontSize: 11 }}>
            {i.clave}
          </div>
        </>
      ),
    },
    {
      campo: 'modalidad',
      titulo: 'Modalidad',
      ordenable: true,
      celda: (i) => txt(TXT_IDC_MODALIDAD, i.modalidad),
    },
    { campo: 'ciudad', titulo: 'Ciudad base', secundaria: true },
    {
      campo: 'estatus',
      titulo: 'Estatus',
      ordenable: true,
      celda: (i) => (
        <span className={`tag ${CLS_IDC_ESTATUS[i.estatus] ?? 't-mute'}`}>
          {txt(TXT_IDC_ESTATUS, i.estatus)}
        </span>
      ),
    },
    {
      campo: 'epp',
      titulo: 'EPP',
      secundaria: true,
      celda: (i) =>
        i.epp ? (
          <span className={`tag ${CLS_EPP[i.epp] ?? 't-mute'}`}>{txt(TXT_EPP, i.epp)}</span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      /* Lo que trae encima y lo que tiene en curso: es lo que se pregunta
         antes de asignarle un ticket mas. */
      campo: 'piezas_resguardo',
      titulo: 'Carga',
      num: true,
      celda: (i) => (
        <>
          <div>
            {i.tickets_abiertos === 0 ? (
              <span className="muted">libre</span>
            ) : (
              <b>
                {entero(i.tickets_abiertos)} {i.tickets_abiertos === 1 ? 'ticket' : 'tickets'}
              </b>
            )}
          </div>
          <div className="muted" style={{ fontSize: 11 }}>
            {i.tiene_resguardo ? (
              i.piezas_resguardo === 0 ? (
                <span className="tag t-warm">sin equipo</span>
              ) : (
                `${entero(i.piezas_resguardo)} piezas`
              )
            ) : (
              'sin resguardo'
            )}
          </div>
        </>
      ),
    },
    {
      campo: 'costo_mensual_centavos',
      titulo: 'Costo mensual',
      num: true,
      monto: true,
      secundaria: true,
      celda: (i) => mask(i.costo_mensual_centavos, i.moneda),
    },
  ];

  return (
    <>
      <Pagehead />
      <Tabla<Idc>
        ruta="idc"
        que="la plantilla de IDC"
        icono="🤝"
        columnas={columnas}
        llave={(i) => i.clave}
        buscable
        csv
        orden="nombre"
        recarga={recarga}
        {...(escribe ? { alElegir: (fila: Idc) => setPanel({ fila }) } : {})}
        acciones={
          escribe ? (
            <button
              type="button"
              className="btn pri"
              onClick={() => setPanel({})}
              data-prueba="alta"
            >
              + IDC
            </button>
          ) : undefined
        }
      />

      {/* El pago por servicio vive DENTRO del dialogo del ingeniero.
      
          Dentro y no debajo: el dialogo es `aria-modal="true"`, asi que lo que
          se dibuje atras queda visible y fuera de alcance —el puntero no llega
          y un lector de pantalla tampoco—. Ya costo media hora con el panel de
          tarifas de proyecto.
      
          Solo a quien recibe montos: es lo que se le PAGA a una persona, el
          dato mas sensible de esta pantalla. La ruta lo niega igual, asi que
          esto es la puerta y no la cerradura. */}
      {panel && (
        <Formulario
          ruta="idc"
          campos={campos}
          titulo={panel.fila ? panel.fila.nombre : 'Alta de IDC'}
          {...(panel.fila && veMontos
            ? { extra: <TarifasIdc clave={panel.fila.clave} puedeEscribir={escribe} /> }
            : {})}
          actual={panel.fila}
          llave={panel.fila?.clave}
          alCerrar={() => setPanel(null)}
          alGuardar={() => {
            setPanel(null);
            setRecarga(recarga + 1);
          }}
        />
      )}
    </>
  );
}
